import { IRouteAPIResponse } from "@/src/types";
import { useCallback, useState } from "react";

type UseRequestParams = {
  urlParams?: Record<string, string | number>;
  body?: Record<string, any>;
};


function interpolateURL(url: string, params?: Record<string, string | number>) {
  if (!params) return url;
  return Object.entries(params).reduce(
    (acc, [key, val]) => acc.replace(`:${key}`, encodeURIComponent(String(val))),
    url
  );
}

export const useRequest = ({baseUrl, method = "POST", defaultBody, isDirectlyLoading =false}:{baseUrl: string, method?:string, defaultBody?: Record<string, any>, isDirectlyLoading?:boolean}) => {
  const [data, setData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(isDirectlyLoading);
  const [error, setError] = useState<string | null>(null);

  const trigger = useCallback(async ({ urlParams, body }: UseRequestParams = {}) => {
    setIsLoading(true);
    setError(null);

    let finalUrl = baseUrl;

    const options: RequestInit = {
      method,
      headers: { "Content-Type": "application/json" },
    };

    if (method === "GET" && urlParams) {
      finalUrl = interpolateURL(finalUrl, urlParams);
    } else if (method !== "GET" && body) {
      options.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(finalUrl,  options);

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result:IRouteAPIResponse = await response.json();
      if (!result || typeof result !== "object") {
        throw new Error("Invalid response format");
      }
      if(!result.success) {
        throw new Error(result.error || "Request failed");
      }
      setData(result.data ?? null); // fallback in case `data` is not nested
      return result.data;
    } catch (err) {
      setError((err as Error).message || "Error occurred during request");
      setData(null);
    } finally {
      setIsLoading(false);
    }
    return null;
  }, [baseUrl, method, setData, setIsLoading, setError]);

  return { data, isLoading, error, trigger };
};